// @file Surface comments store
import { captureFetchException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { COMMENT_BODY_CHAR_LIMIT } from '@@/bits/numbers'
import { getVuexStore } from '@@/bits/pinia'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import PadletApi from '@@/surface/padlet_api'
import type { Comment, Id } from '@@/types'
import { OzConfirmationDialogBoxButtonScheme } from '@@/vuexstore/modules/global_confirmation_dialog'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useCommentsStore = defineStore('comments', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()
  const { genericFetchError, setSnackbar } = useGlobalSnackbarStore()

  const commentIds = computed<string[]>(() => surfaceVuexStore?.getters['comment/commentIds'])
  const commentEntities = computed<Record<Id, Comment>>(() => surfaceVuexStore?.state.comment.commentEntities ?? {})

  async function createCommentV2({ postId, htmlBody }: { postId: Id; htmlBody: string }): Promise<void> {
    if (htmlBody.length > COMMENT_BODY_CHAR_LIMIT) {
      showCommentCharacterLimitSnackbar()
      return
    }

    // tempId will be used to identify the comment in the frontend until it is persisted,
    // after which, we will use the server-provided Id.
    const tempId = `new_${Date.now()}`

    const newComment = {
      wish_id: postId,
      html_body: htmlBody,
    }
    surfaceVuexStore?.commit('comment/ADD_PENDING_COMMENT', { id: tempId, ...newComment })

    try {
      const post = surfaceVuexStore?.getters['post/getPostByServerId'](postId)
      void surfaceVuexStore?.dispatch('comment/updateNewCommentBody', { postCid: post.cid, body: '' })

      const response = await PadletApi.CommentV2.create(newComment)
      const comment = (response.data as JsonAPIResource<Comment>).attributes

      surfaceVuexStore?.commit('comment/ADD_COMMENT', { comment, tempId })
    } catch (e) {
      void genericFetchError()
      captureFetchException(e, { source: 'SurfaceCreateCommentV2' })
      const post = surfaceVuexStore?.getters['post/getPostByServerId'](postId)
      if (post == null || post.cid == null) {
        void surfaceVuexStore?.commit('comment/REMOVE_PENDING_COMMENT', { commentId: tempId })
      } else {
        void surfaceVuexStore?.commit('comment/REVERT_PENDING_COMMENT', { commentId: tempId, postCid: post.cid })
      }
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState', null, { root: true })
    }
  }

  function startEditingCommentV2({ commentId }: { commentId: Id }): void {
    surfaceVuexStore?.commit('comment/START_EDITING_COMMENT', commentId)
    void surfaceVuexStore?.dispatch('comment/updateEditedDraftCommentBody', {
      commentId,
      body: commentEntities.value[commentId].html_body ?? commentEntities.value[commentId].body,
    })
  }

  async function editCommentV2({ id, htmlBody }: { id: Id; htmlBody: string }): Promise<void> {
    // Comparing to existingComment so that only comments that already exceed character limit can continue to exceed character limit
    const existingCommentBody = commentEntities.value[id]?.html_body ?? ''
    if (existingCommentBody.length <= COMMENT_BODY_CHAR_LIMIT && htmlBody.length > COMMENT_BODY_CHAR_LIMIT) {
      showCommentCharacterLimitSnackbar()
      return
    }

    try {
      void surfaceVuexStore?.commit('comment/UPDATE_COMMENT', { id, obj: { html_body: htmlBody } })
      void surfaceVuexStore?.commit('comment/STOP_EDITING_COMMENT')
      const {
        data: {
          attributes: { html_body: newBody },
        },
      } = await PadletApi.CommentV2.update(id, { html_body: htmlBody })
      void surfaceVuexStore?.commit('comment/UPDATE_COMMENT', { id, obj: { body: newBody } })
    } catch (e) {
      void genericFetchError()
      captureFetchException(e, { source: 'SurfaceEditCommentV2' })
      startEditingCommentV2({ commentId: id })
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void surfaceVuexStore?.dispatch('nativeApp/postSurfaceState', null, { root: true })
    }
  }

  function showCommentCharacterLimitSnackbar(): void {
    void setSnackbar({
      message: __('Comment character limit reached'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function askToDeleteComment({ commentId }: { commentId: Id }): void {
    void surfaceVuexStore?.dispatch(
      'confirmationDialog/openConfirmationDialog',
      {
        title: __('Delete this comment?'),
        body: __('Are you sure you want to delete this comment? This cannot be undone!'),
        confirmButtonText: __('Delete'),
        cancelButtonText: __('Cancel'),
        buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
        afterConfirmActions: [{ type: 'comment/deleteComment', payload: { commentId } }],
      },
      { root: true },
    )
  }

  return {
    commentIds,

    // Actions
    createCommentV2,
    startEditingCommentV2,
    editCommentV2,
    askToDeleteComment,
  }
})
